import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchGifts, HouseholdGift } from '../../api/giftsApi';
import { enqueueRender } from '../../api/rendersApi';
import { isAbortError, toApiError } from '../../api/http';
import { useBedtimeContext } from '../../context/BedtimeContext';
import { useApp } from '../../context/AppContext';
import { ArtworkTile, PillButton, PolicyDisclosure, Shelf, Sheet } from '../../components/ui';
import './GiftShelf.css';

/** Adult-only shelf. All playback and approval stay in the authorized render flow. */
const GiftShelf: React.FC = () => {
  const { state } = useApp();
  const context = useBedtimeContext();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [connected, setConnected] = useState(navigator.onLine);
  useEffect(() => { const update = () => setConnected(navigator.onLine); window.addEventListener('online', update); window.addEventListener('offline', update); return () => { window.removeEventListener('online', update); window.removeEventListener('offline', update); }; }, []);
  const [gifts, setGifts] = useState<HouseholdGift[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [preparing, setPreparing] = useState<string | null>(null);
  const request = useRef<AbortController | null>(null);
  const pair = context.pair;
  const selectionKey = `${state.user?.id}:${pair?.householdId}:${pair?.listenerId}:${pair?.voiceId}`;
  const selection = useRef(selectionKey);
  selection.current = selectionKey;

  useEffect(() => {
    const controller = new AbortController();
    setGifts([]);
    setError(null);
    if (state.user?.id && !state.offlineSession) {
      fetchGifts(controller.signal).then((rows) => {
        if (!controller.signal.aborted) setGifts(rows);
      }).catch((err) => {
        if (!controller.signal.aborted && !isAbortError(err)) setError('Gifts could not be checked. Your saved stories are still below.');
      });
    }
    return () => controller.abort();
  }, [state.user?.id, state.offlineSession, refresh]);

  useEffect(() => {
    request.current?.abort();
    setPreparing(null);
    setSelectedId(null);
    return () => request.current?.abort();
  }, [selectionKey]);

  const prepare = async (gift: HouseholdGift) => {
    if (!pair || pair.householdId !== gift.household_id || context.status !== 'ready' || (request.current && !request.current.signal.aborted) || preparing || state.offlineSession || !navigator.onLine || gift.status !== 'active' || !gift.creation_available || gift.approved_render_id || gift.preparation_render_id) return;
    const controller = new AbortController();
    const key = selectionKey;
    request.current = controller;
    setPreparing(gift.id);
    setError(null);
    try {
      const result = await enqueueRender(gift.story_id, pair.voiceId, pair.listenerId, controller.signal, gift.id, undefined, gift.content_scope?.content_sha256);
      if (!controller.signal.aborted && selection.current === key) navigate(result.render_id ? `/renders/${result.render_id}` : '/renders');
    } catch (err) {
      if (!controller.signal.aborted && selection.current === key && !isAbortError(err)) setError(toApiError(err).message);
    } finally {
      if (request.current === controller) { request.current = null; setPreparing(null); }
    }
  };

  const visible = gifts.filter((gift) => !pair || gift.household_id === pair.householdId);
  if (!visible.length && !error) return null;
  const listener = context.listeners.find((item) => item.listener_id === pair?.listenerId);
  const voice = context.voices.find((item) => item.voice_id === pair?.voiceId);
  const gift = visible.find(item => item.id === selectedId);
  const active = gift?.status === 'active';
  const replayAvailable = active || (gift?.status === 'review_required' && Boolean(gift.approved_render_id));
  const canPrepare = gift && active && gift.creation_available && !gift.approved_render_id;
  const choosePair = () => { if (!gift) return; setSelectedId(null); context.openPairDialog({ householdId: gift.household_id }); };
  return <section className="gift-shelf" aria-label="Books gifted to your household">
    {error && !gift && <div role="alert"><p>{error}</p><PillButton onClick={() => setRefresh(value => value + 1)}>Check gifts again</PillButton></div>}
    <Shelf id="family-gifts" title="Gifts for your family" items={visible} getKey={item => item.id} aspectRatio="4/3" renderItem={item => <ArtworkTile title={item.title} artwork={`/api/stories/${encodeURIComponent(item.story_id)}/artwork`} aspectRatio="4/3" metadata={[item.gifted_by ? `From ${item.gifted_by}` : 'A bedtime gift']} onActivate={() => setSelectedId(item.id)} />} />
    <Sheet open={Boolean(gift)} title={gift?.title || 'Your gift'} onClose={() => { if (!preparing) setSelectedId(null); }}>
      {gift && <div className="gift-shelf__detail">
        <p>{gift.gifted_by ? `A story picked for you by ${gift.gifted_by}.` : 'A story picked just for you.'}</p>
        <p>{gift.content_scope?.label || 'Included content is awaiting review.'}</p>
        {error && <p role="alert">{error}</p>}
        {(state.offlineSession || !connected) && <p role="status">Connect to the internet to make this gift story. Your saved stories are still available.</p>}
        {!replayAvailable ? <p>This gift is currently unavailable. Contact support for help with its payment status.</p> : gift.approved_render_id ? <>
          <p>Your approved bedtime is yours to replay in Luma, even if your subscription ends. Voice permission still applies.</p>
          <PillButton variant="primary" to={`/renders/${gift.approved_render_id}`}>Open approved bedtime</PillButton>
        </> : gift.preparation_render_id ? <>
          <p>Your gift is being prepared or is ready for your review. It will only be used when you approve the bedtime.</p>
          <PillButton variant="primary" to={`/renders/${gift.preparation_render_id}`}>Review gift bedtime</PillButton>
        </> : canPrepare ? <>
          <p>Make it personal with a voice they love.</p>
          {context.status === 'ready' && pair?.householdId === gift.household_id ? <>
            <p>For <strong>{listener?.display_name || 'your listener'}</strong>, with <strong>{voice?.voice_name || 'your Family voice'}</strong>.</p>
            <div className="gift-shelf__actions"><PillButton variant="primary" disabled={state.offlineSession || !connected} busy={preparing === gift.id} busyLabel="Making this gift story…" onClick={() => void prepare(gift)}>Make this gift story</PillButton>
            <PillButton disabled={Boolean(preparing)} onClick={choosePair}>Change listener or voice</PillButton></div>
          </> : <>
            <p>Your book stays here while you choose an authorized Family voice and listener and save their personal moments.</p>
            <PillButton variant="primary" onClick={choosePair}>Choose listener and voice</PillButton>
          </>}
          <PolicyDisclosure title="Gift details"><p>Includes one initial personalized bedtime and ongoing in-app replay of the bedtime you approve, without a subscription. A bedtime is a complete short story or the identified chapter or segment, not an entire chapter book. Preparation, failed synthesis, QA retries, defect repairs and replay use no additional allowance.</p></PolicyDisclosure>
        </> : <p>Your gift is being updated. Check again shortly.</p>}
      </div>}
    </Sheet>
  </section>;
};

export default GiftShelf;
